/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {
  useCallback,
  useEffect,
  useRef
} from 'react';
import {useSelector} from 'react-redux';
import debounce from 'lodash.debounce';
import {error} from '@ion/core/src/logger/logger';
import Device from '@ion/core/src/services/Device';
import {AgGridType} from '@ion/dls/src/grid/IonGrid';
import {selectedAssetSelector} from '@/store/selectors/tracking-selectors';
import {scrollToItemId} from '../../trackingUtils';

/**
 * @useScrollToItem
 * React Hook to scroll to item on given gridApi
 *
 * Handle click events that select an asset that come from outside the grid
 * (ex. the map, search bar) and highlight the corresponding grid row.
 *
 *  ```typescript
 *   import {useScrollToItem} from '@/routes/tracking/hooks';
 *
 *   const {selectedAsset, rowClicked, scrollToItem} = useScrollToItem(gridApi, interval);
 *  ```
 */
export const useScrollToItem = (gridApi: AgGridType, interval: number = 0) => {
  const selectedAsset = useSelector(selectedAssetSelector);
  const rowClicked    = useRef(false);

  const scrollToItem = useCallback(async(gridApi: AgGridType, selectedAsset: Device, interval: number = 0) => {
    try {
      if (selectedAsset) {
        const scrollTo = setTimeout(() => {
          const rowNode = gridApi?.getRowNode(selectedAsset?.getItemValue({
            deviceAttribute : scrollToItemId.deviceId,
            cvsAttribute    : scrollToItemId.assetId
          })?.toString());
          gridApi?.deselectAll();
          if (rowNode) {
            gridApi.setGridOption('loading', false);
            gridApi.hideOverlay();
            rowNode.setSelected(true, true);
            gridApi.ensureIndexVisible(rowNode.rowIndex, 'top');
          }
          clearTimeout(scrollTo);
        }, interval);
      } else {
        gridApi?.deselectAll();
      }
    } catch (err) {
      error(err.message);
      if (gridApi) {
        gridApi.deselectAll();
        gridApi.setGridOption('loading', false);
        gridApi.hideOverlay();
      }
    }
  }, [gridApi]);

  const manageScroll = useCallback(debounce(() => {
    if (window && gridApi && !rowClicked.current) {
      scrollToItem(gridApi, selectedAsset, interval);
    } else {
      rowClicked.current = false;
    }
  }, 400), [window, gridApi, selectedAsset, interval, scrollToItem]);

  useEffect(() => {
    manageScroll();
  }, [selectedAsset, rowClicked.current]);

  useEffect(() => {
    if (gridApi) {
      gridApi.addEventListener('modelUpdated', manageScroll);
      return () => {
        gridApi.removeEventListener('modelUpdated', manageScroll);
      };
    }
  }, [gridApi, manageScroll]);

  return {selectedAsset, rowClicked, scrollToItem};
};
